/**
 * North Macedonia salary calculator — 2026
 * Ported from Elm (gross.elm) — identical logic.
 * All amounts in MKD (integers).
 */

export const AVERAGE_SALARY = 69_141;
export const PERSONAL_EXEMPTION = 10_932;
export const MIN_NET = 24_379;
export const MIN_GROSS = 34_571;
export const MAX_BASE = AVERAGE_SALARY * 16;

export const RATES = {
  pension: 0.188,
  health: 0.075,
  unemployment: 0.012,
  sickness: 0.005,
} as const;

export const TAX_RATE = 0.10;

export interface SalaryBreakdown {
  grossSalary: number;
  pensionContribution: number;
  healthContribution: number;
  unemploymentContribution: number;
  sicknessContribution: number;
  totalContributions: number;
  grossMinusContributions: number;
  personalExemption: number;
  taxBase: number;
  personalIncomeTax: number;
  totalDeductions: number;
  netSalary: number;
}

export function grossToNet(gross: number): SalaryBreakdown {
  const base = Math.min(gross, MAX_BASE);
  const pensionContribution = Math.round(base * RATES.pension);
  const healthContribution = Math.round(base * RATES.health);
  const unemploymentContribution = Math.round(base * RATES.unemployment);
  const sicknessContribution = Math.round(base * RATES.sickness);
  const totalContributions =
    pensionContribution + healthContribution + unemploymentContribution + sicknessContribution;
  const grossMinusContributions = gross - totalContributions;
  const personalExemption = PERSONAL_EXEMPTION;
  const taxBase = gross - totalContributions - personalExemption;
  const personalIncomeTax = Math.round(taxBase * TAX_RATE);
  const totalDeductions = totalContributions + personalIncomeTax;
  const netSalary = gross - totalDeductions;

  return {
    grossSalary: gross,
    pensionContribution,
    healthContribution,
    unemploymentContribution,
    sicknessContribution,
    totalContributions,
    grossMinusContributions,
    personalExemption,
    taxBase,
    personalIncomeTax,
    totalDeductions,
    netSalary,
  };
}

function binSearch(target: number, lo: number, hi: number): number {
  while (lo <= hi) {
    const mid = lo + Math.floor((hi - lo) / 2);
    const net = grossToNet(mid).netSalary;
    if (net === target) return mid;
    if (net < target) lo = mid + 1;
    else hi = mid - 1;
  }
  return lo;
}

export function netToGross(net: number): SalaryBreakdown {
  const clamped = Math.max(MIN_NET, net);
  const gross = binSearch(clamped, clamped, clamped * 10);
  return grossToNet(gross);
}

export function formatMKD(amount: number): string {
  return new Intl.NumberFormat("mk-MK").format(amount);
}
